import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Car, Star, Pencil, Trash2, MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { VehicleFormDialog } from './VehicleFormDialog';

interface Vehicle {
  id: string;
  user_id: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  nickname: string;
  modifications: string;
  photo_urls: any;
  primary_photo_url: string;
  is_featured: boolean;
}

interface VehicleCardProps {
  vehicle: Vehicle;
  onUpdate: (data: any) => void;
  onDelete: (id: string) => void;
  onSetFeatured: (id: string) => void;
}

export const VehicleCard = ({ vehicle, onUpdate, onDelete, onSetFeatured }: VehicleCardProps) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleDelete = () => {
    onDelete(vehicle.id);
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <Card className="overflow-hidden group hover:shadow-lg transition-shadow">
        <div className="relative">
          {vehicle.primary_photo_url ? (
            <AspectRatio ratio={16 / 9}>
              <img
                src={vehicle.primary_photo_url}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className="object-cover w-full h-full photo-signature"
              />
            </AspectRatio>
          ) : (
            <AspectRatio ratio={16 / 9}>
              <div className="bg-muted flex items-center justify-center h-full">
                <Car className="h-16 w-16 text-muted-foreground opacity-30" />
              </div>
            </AspectRatio>
          )}

          {vehicle.is_featured && (
            <Badge className="absolute top-3 right-3 bg-amber-500">
              <Star className="h-3 w-3 mr-1 fill-current" />
              Featured
            </Badge>
          )}

          <div className="absolute top-3 left-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="secondary" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                {!vehicle.is_featured && (
                  <DropdownMenuItem onClick={() => onSetFeatured(vehicle.id)}>
                    <Star className="h-4 w-4 mr-2" />
                    Set as Featured
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-foreground mb-1">
            {vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          </h3>
          {vehicle.nickname && (
            <p className="text-sm text-muted-foreground mb-2">
              {vehicle.year} {vehicle.make} {vehicle.model}
              {vehicle.trim && ` ${vehicle.trim}`}
            </p>
          )}
          {vehicle.modifications && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-2">
              {vehicle.modifications}
            </p>
          )}
        </div>
      </Card>

      <VehicleFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSubmit={(data) => {
          onUpdate({ id: vehicle.id, ...data });
          setIsEditDialogOpen(false);
        }}
        userId={vehicle.user_id}
        vehicle={vehicle}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium">
                {vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
